import moment from "moment"

import {
    ICalculatorEmailResultsData,
    ICalculatorGetCalculationsData,
} from "@common-shared/types/requestData/calculatorRequestData.types"
import { IFileToUploadRequestData } from "@common-shared/types/requestData/commonRequestData.types"
import type { ICalculatorResult } from "@common-shared/types/responseData/commonResponseData.types"

import { UtilHelper } from "@front-shared/helpers/util.helper"

import { GENERAL_CONFIG } from "../config/general.config"
import { API_ROUTES } from "../config/routes.config"
import { HttpHelper } from "../helpers/http.helper"
import type { ICalculatorInputData } from "../types/data.types"

export class CalculatorService {
    /**
     * Save id card to S3 and starts manual eligibility check
     */
    static async startManualEligibility(data: {
        idCard: IFileToUploadRequestData
        inputData: ICalculatorInputData
        email: string
    }): Promise<any> {
        const newInputData = { ...data.inputData } as any

        newInputData.practiceId = data.inputData.practice?.id
        newInputData.payerId = data.inputData.payer.payerId
        newInputData.healthProviderId = data.inputData.healthProvider.id
        newInputData.dob = moment(data.inputData.dob).valueOf()

        delete newInputData.practice
        delete newInputData.payer
        delete newInputData.healthProvider

        const result = await HttpHelper.sendRequest(
            API_ROUTES.manualEligibilityUrl,
            {
                idCard: data.idCard,
                email: data.email,
                ...newInputData,
            },
            "POST",
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Get calculation data
     */
    static async getCalculationsData(
        inputData: ICalculatorInputData,
        embedKey?: string,
    ): Promise<ICalculatorResult | undefined> {
        const { firstName, lastName, sessionCharge, memberId, cptCode } =
            inputData

        const data: ICalculatorGetCalculationsData = {
            firstName,
            lastName,
            dob: UtilHelper.dateToMysqlFormat(inputData.dob), // YYYY-MM-DD
            healthProviderId: inputData.healthProvider?.id,
            practiceId: inputData.practice?.id,
            payerId: inputData.payer.payerId,
            memberId,
            sessionCharge,
            cptCode,
        }

        const result = await HttpHelper.sendRequest(
            API_ROUTES.calculateUrl,
            data,
            "GET",
            embedKey
                ? {
                      [GENERAL_CONFIG.headers.embedKey]: embedKey,
                  }
                : undefined,
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Email results (generated pdf) to some email address
     */
    static async emailResults(
        email: string,
        resultingPdf: IFileToUploadRequestData,
    ): Promise<any> {
        const requestData: ICalculatorEmailResultsData = {
            email,
            resultingPdf,
        }

        const result = await HttpHelper.sendRequest(
            API_ROUTES.emailResultsUrl,
            requestData,
            "POST",
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }
}
